import clsx from 'clsx'
import { useRouter } from 'next/router'

/* blocks */
import Button from '@/atoms/Button/Button'
import { useToast } from '@/hooks/useToast'
import TextField from '@/molecules/FormikFields/TextField'
import SelectField from '@/molecules/FormikFields/SelectField'

/* utils */
import { dataLayerFormSubmit } from '@/utils/functions/dataLayerEvents'
import { FormikHelpers, Formik, Form } from 'formik'
import { FormValues, initialFormValues, validationSchema } from './schema'
import { numEmployeesOptions } from '@/utils/constants/form'

/* styling */
import styles from './_.module.css'

function RegistrationForm() {
  const router = useRouter()
  const { toast, showToast } = useToast()

  const submitToMailChimp = async (url: string, values: FormValues) => {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        ...values,
        sourceUrl: router.asPath,
        status: 'subscribed'
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const data = await response.json()
    return { ok: response.ok, data }
  }

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    try {
      const result = await submitToMailChimp(
        '/api/forms/index-25-reg-form/',
        values
      )
      if (!result.ok) {
        showToast({
          title: result.data.message,
          type: 'error',
          swipeDirection: 'up'
        })
        return
      }
      showToast({
        title: (
          <>
            Thank you for registering for Index’25 Virtual Conference! 🎉 Check
            your inbox for confirmation details
          </>
        ),
        type: 'success',
        swipeDirection: 'up'
      })
      dataLayerFormSubmit({
        formName: 'Registration Form',
        formValues: values
      })
      helpers.resetForm()
    } catch (error) {
      console.error(error)
    }
  }
  return (
    <div
      id="index-25-reg-form"
      className="font-body bg-[#0f1135] text-white | shadow-lg rounded-lg p-7 md:p-14"
    >
      <div>
        <Formik<FormValues>
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form name="Events Form" id="events-form">
              <SelectField
                name="proficiencyInAI"
                label="Who Are You?"
                placeholder="Select who you are"
                options={whoAreYouOptions.map(item => ({
                  name: item.label,
                  value: item.value
                }))}
                className={clsx('relative mb-6', styles.formField)}
                required
              />
              <div className="md:flex md:gap-5">
                <TextField
                  name="firstName"
                  className={clsx('mb-5', styles.formField)}
                  label="First Name"
                  required
                />
                <TextField
                  name="lastName"
                  className={clsx('mb-5', styles.formField)}
                  label="Last Name"
                  required
                />
              </div>
              <div className="md:flex md:gap-5">
                <TextField
                  name="email"
                  className={clsx('mb-5', styles.formField)}
                  label="Email Address"
                  required
                />
                <TextField
                  name="company"
                  className={clsx('mb-5', styles.formField)}
                  label="Company Name"
                  required
                />
              </div>
              <div className="md:flex md:gap-5">
                <TextField
                  name="linkedIn"
                  className={clsx('mb-5', styles.formField)}
                  label="LinkedIn Profile"
                  placeholder="linkedin.com/in/yourname"
                  required
                />
                <TextField
                  name="designation"
                  className={clsx('mb-5', styles.formField)}
                  label="Designation"
                  required
                />
              </div>
              <div className="md:flex md:gap-5">
                <SelectField
                  name="teamSize"
                  label="Team Size"
                  placeholder="Select team size"
                  options={numEmployeesOptions.map(item => ({
                    name: item.name,
                    value: item.value
                  }))}
                  className={clsx('relative mb-6', styles.formField)}
                  required
                />
              </div>
              <div>
                <Button
                  className={clsx(
                    'text-xl font-medium | rounded! | w-full',
                    styles.registerBtn
                  )}
                  size="large"
                  htmlType="submit"
                >
                  {isSubmitting ? 'Loading...' : 'Register Now'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
        {toast}
      </div>
    </div>
  )
}

export const whoAreYouOptions = [
  { label: 'I work at a Brand', value: 'I work at a Brand' },
  {
    label: 'I work at an Agency / Consultancy',
    value: 'I work at an Agency / Consultancy'
  },
  { label: 'I’m a Freelancer / Creator', value: 'I’m a Freelancer / Creator' },
  { label: 'Others', value: 'Others' }
]

export default RegistrationForm
